import { Controller } from "@hotwired/stimulus"
import db from "../field_db"
import { logEvent } from "../field_logger"

// Displays detailed IndexedDB storage statistics.
//
// Targets:
//   deviceUsed / deviceQuota / deviceBar -- device storage summary
//   cachedCount / cachedSize             -- cached_responses
//   pendingResponsesCount / pendingResponsesBreakdown
//   pendingResultsCount / pendingResultsBreakdown
//   pendingImagesCount / pendingImagesSize / pendingImagesBreakdown
//   syncLogCount / syncLogList
//   lastSynced
//   clearCacheBtn

const PAGE_CACHE          = 'win-field-pages-v3'
const MISSION_ASSETS_CACHE = 'win-field-mission-assets-v1'

export default class extends Controller {
  static targets = [
    "deviceUsed", "deviceQuota", "deviceBar",
    "cachedCount", "cachedSize",
    "pendingResponsesCount", "pendingResponsesBreakdown",
    "pendingResultsCount", "pendingResultsBreakdown",
    "pendingImagesCount", "pendingImagesSize", "pendingImagesBreakdown",
    "syncLogCount", "syncLogList",
    "lastSynced",
    "clearCacheBtn",
    "swAssetsCacheCount", "swAssetsCacheSize",
    "swPageCacheCount", "swPageCacheList", "swPageCacheFilter",
    "swShellCacheCount", "swShellCacheList", "swShellCacheFilter",
  ]

  async connect() {
    await Promise.all([
      this._loadDevice(),
      this._loadCachedResponses(),
      this._loadPendingResponses(),
      this._loadPendingResults(),
      this._loadPendingImages(),
      this._loadSyncLog(),
      this._loadSwCaches(),
    ])

    const lastSync = localStorage.getItem("field:last-prefetch")
    if (this.hasLastSyncedTarget) {
      this.lastSyncedTarget.textContent = lastSync
        ? new Date(lastSync).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })
        : "Never"
    }
  }

  async clearCache() {
    if (!confirm("Clear all cached surveys? You will need to sync again before going offline.")) return

    logEvent("cache", "Offline cache cleared by user").catch(() => {})
    await db.cached_responses.clear()
    await db.cached_places.clear()
    localStorage.removeItem("field:last-prefetch")
    await this._loadCachedResponses()
    if (this.hasLastSyncedTarget) this.lastSyncedTarget.textContent = "Never"
  }

  // -- private --

  async _loadDevice() {
    try {
      const estimate = await navigator.storage?.estimate()
      if (!estimate) return

      const used  = estimate.usage  || 0
      const quota = estimate.quota  || 0
      const pct   = quota > 0 ? Math.round((used / quota) * 100) : 0

      if (this.hasDeviceUsedTarget)  this.deviceUsedTarget.textContent  = this._bytes(used)
      if (this.hasDeviceQuotaTarget) this.deviceQuotaTarget.textContent = this._bytes(quota)
      if (this.hasDeviceBarTarget) {
        this.deviceBarTarget.style.width = `${Math.min(pct, 100)}%`
        this.deviceBarTarget.className = pct > 80
          ? "h-2 rounded-full bg-red-400 transition-all"
          : pct > 50
            ? "h-2 rounded-full bg-amber-400 transition-all"
            : "h-2 rounded-full bg-emerald-400 transition-all"
      }
    } catch { /* Storage API unavailable */ }
  }

  async _loadCachedResponses() {
    try {
      const records = await db.cached_responses.toArray()
      const places  = await db.cached_places.toArray()
      const count   = records.length

      if (this.hasCachedCountTarget) {
        const placeCount = places.length
        this.cachedCountTarget.textContent = count === 0
          ? "None"
          : `${count} mission${count !== 1 ? "s" : ""} across ${placeCount} place${placeCount !== 1 ? "s" : ""}`
      }

      // Estimate size by JSON-serialising each record (responses + places)
      if (this.hasCachedSizeTarget) {
        if (count === 0 && places.length === 0) {
          this.cachedSizeTarget.textContent = "0 B"
        } else {
          const bytes = records.reduce((sum, r) => sum + this._jsonBytes(r), 0)
                      + places.reduce((sum, p) => sum + this._jsonBytes(p), 0)
          this.cachedSizeTarget.textContent = this._bytes(bytes)
        }
      }
    } catch { /* IndexedDB unavailable */ }
  }

  async _loadPendingResponses() {
    try {
      const all     = await db.pending_responses.toArray()
      const count   = all.length
      const pending = all.filter(r => r.status === "pending").length
      const failed  = all.filter(r => r.status === "failed").length
      const syncing = all.filter(r => r.status === "syncing").length

      if (this.hasPendingResponsesCountTarget) {
        this.pendingResponsesCountTarget.textContent = count === 0 ? "None" : count
      }
      if (this.hasPendingResponsesBreakdownTarget) {
        this.pendingResponsesBreakdownTarget.textContent = count === 0
          ? "Queue is empty"
          : [pending && `${pending} pending`, syncing && `${syncing} syncing`, failed && `${failed} failed`]
              .filter(Boolean).join(" · ")
      }
    } catch { /* IndexedDB unavailable */ }
  }

  async _loadPendingResults() {
    try {
      const all   = await db.pending_results.toArray()
      const count = all.length
      const failed = all.filter(r => r.status === "failed").length

      if (this.hasPendingResultsCountTarget) {
        this.pendingResultsCountTarget.textContent = count === 0 ? "None" : count
      }
      if (this.hasPendingResultsBreakdownTarget) {
        this.pendingResultsBreakdownTarget.textContent = count === 0
          ? "Queue is empty"
          : failed ? `${count - failed} pending · ${failed} failed` : `${count} pending`
      }
    } catch { /* IndexedDB unavailable */ }
  }

  async _loadPendingImages() {
    try {
      const all     = await db.pending_images.toArray()
      const count   = all.length
      const pending = all.filter(r => r.status === "pending").length
      const failed  = all.filter(r => r.status === "failed").length
      const uploading = all.filter(r => r.status === "uploading").length

      if (this.hasPendingImagesCountTarget) {
        this.pendingImagesCountTarget.textContent = count === 0 ? "None" : count
      }

      if (this.hasPendingImagesSizeTarget) {
        // Sum blob sizes where available
        let bytes = 0
        for (const record of all) {
          // Vault rows carry no Blob (card #3995): their size is in the metadata.
          if (record.blob instanceof Blob) bytes += record.blob.size
          else if (record.blobStore === "vault") bytes += record.sizeBytes || record.byteLength || 0
        }
        this.pendingImagesSizeTarget.textContent = bytes > 0 ? this._bytes(bytes) : "—"
      }

      if (this.hasPendingImagesBreakdownTarget) {
        this.pendingImagesBreakdownTarget.textContent = count === 0
          ? "Queue is empty"
          : [pending && `${pending} pending`, uploading && `${uploading} uploading`, failed && `${failed} failed`]
              .filter(Boolean).join(" · ")
      }
    } catch { /* IndexedDB unavailable */ }
  }

  async _loadSyncLog() {
    try {
      const count   = await db.sync_log.count()
      const entries = await db.sync_log.orderBy("syncedAt").reverse().limit(10).toArray()

      if (this.hasSyncLogCountTarget) {
        this.syncLogCountTarget.textContent = count === 0 ? "—" : `${count} total`
      }

      if (this.hasSyncLogListTarget) {
        if (entries.length === 0) {
          this.syncLogListTarget.innerHTML = `<p class="px-4 py-3 text-xs text-slate-400">No syncs recorded yet</p>`
        } else {
          this.syncLogListTarget.innerHTML = entries.map(entry => {
            const date   = new Date(entry.syncedAt)
            const time   = date.toLocaleString("en-AU", { dateStyle: "short", timeStyle: "short" })
            const badge  = entry.outcome === "success" ? "text-emerald-600 bg-emerald-50"
                         : entry.outcome === "partial"  ? "text-amber-600 bg-amber-50"
                         :                               "text-red-600 bg-red-50"
            const detail = entry.responsesUploaded > 0
              ? `${entry.responsesUploaded} uploaded${entry.responsesFailed > 0 ? `, ${entry.responsesFailed} failed` : ""}`
              : entry.responsesFailed > 0 ? `${entry.responsesFailed} failed` : "No responses"
            return `
              <div class="px-4 py-2.5 flex items-center justify-between gap-3">
                <div class="flex items-center gap-2 min-w-0">
                  <span class="shrink-0 text-xs font-semibold px-1.5 py-0.5 rounded-full ${badge}">${entry.outcome}</span>
                  <span class="text-xs text-slate-500 truncate">${detail}</span>
                </div>
                <span class="text-xs text-slate-400 shrink-0">${time}</span>
              </div>`
          }).join("")
        }
      }
    } catch { /* IndexedDB unavailable */ }
  }

  filterPageCache(event) {
    this._filterCacheList("swPageCache", event.target.value)
  }

  filterShellCache(event) {
    this._filterCacheList("swShellCache", event.target.value)
  }

  _filterCacheList(prefix, query) {
    const urls = this[`_${prefix}Urls`] || []
    const filtered = query
      ? urls.filter(u => u.includes(query))
      : urls
    const listTarget = this[`has${this._cap(prefix)}ListTarget`] ? this[`${prefix}ListTarget`] : null
    if (listTarget) this._renderUrlList(listTarget, filtered)
  }

  async _loadSwCaches() {
    if (!('caches' in window)) return

    await this._loadSwAssetsCache()
    await this._loadSwCache(PAGE_CACHE, "swPageCache")

    // SHELL_CACHE is versioned (win-field-shell-<APP_VERSION>, see
    // field_service_worker.js) so it changes on every deploy -- reading the
    // app-version meta tag keeps this in step instead of drifting to a stale
    // hardcoded name (card #2970: this had silently drifted to a hardcoded
    // "win-field-shell-v4" that stopped matching the real cache name several
    // versions ago, so the SW Shell Cache panel always read "Empty"
    // regardless of what was actually cached).
    const shellCache = this._shellCacheName()
    if (shellCache) await this._loadSwCache(shellCache, "swShellCache")
  }

  _shellCacheName() {
    const version = document.querySelector('meta[name="app-version"]')?.content
    return version ? `win-field-shell-${version}` : null
  }

  async _loadSwAssetsCache() {
    if (!('caches' in window)) return
    try {
      const cache   = await caches.open(MISSION_ASSETS_CACHE)
      const keys    = await cache.keys()
      const count   = keys.length

      if (this.hasSwAssetsCacheCountTarget) {
        this.swAssetsCacheCountTarget.textContent = count || "Empty"
      }

      if (this.hasSwAssetsCacheSizeTarget) {
        if (count === 0) {
          this.swAssetsCacheSizeTarget.textContent = "Empty cache"
          return
        }
        // Estimate size by reading Content-Length headers where available,
        // falling back to reading response bodies for blob URLs.
        let bytes = 0
        for (const request of keys) {
          const res = await cache.match(request)
          if (!res) continue
          const contentLength = res.headers.get('Content-Length')
          if (contentLength) {
            bytes += parseInt(contentLength, 10)
          } else {
            const blob = await res.clone().blob().catch(() => null)
            if (blob) bytes += blob.size
          }
        }
        this.swAssetsCacheSizeTarget.textContent = bytes > 0 ? this._bytes(bytes) : "Size unknown"
      }
    } catch { /* Cache API unavailable */ }
  }

  async _loadSwCache(cacheName, prefix) {
    try {
      const cache   = await caches.open(cacheName)
      const keys    = await cache.keys()
      const urls    = keys.map(r => new URL(r.url).pathname).sort()

      // Store for filter access
      this[`_${prefix}Urls`] = urls

      const countTarget = this[`has${this._cap(prefix)}CountTarget`]
        ? this[`${prefix}CountTarget`]
        : null
      const listTarget  = this[`has${this._cap(prefix)}ListTarget`]
        ? this[`${prefix}ListTarget`]
        : null

      if (countTarget) countTarget.textContent = urls.length || "Empty"
      if (listTarget) this._renderUrlList(listTarget, urls)
    } catch {
      /* Cache API unavailable */
    }
  }

  _renderUrlList(listTarget, urls) {
    if (urls.length === 0) {
      listTarget.innerHTML = `<p class="text-xs text-slate-400 italic">No entries</p>`
    } else {
      listTarget.innerHTML = urls.map(u =>
        `<p class="text-xs text-slate-600 font-mono truncate">${u}</p>`
      ).join("")
    }
  }

  _cap(str) {
    return str.charAt(0).toUpperCase() + str.slice(1)
  }

  _bytes(n) {
    if (n < 1024)           return `${n} B`
    if (n < 1024 * 1024)    return `${(n / 1024).toFixed(1)} KB`
    if (n < 1024 ** 3)      return `${(n / (1024 * 1024)).toFixed(1)} MB`
    return `${(n / 1024 ** 3).toFixed(2)} GB`
  }

  _jsonBytes(obj) {
    try { return new TextEncoder().encode(JSON.stringify(obj)).length } catch { return 0 }
  }
}
